/**
 * DocumentPreviewDialog — in-app preview for a short-lived blob URL (image, PDF,
 * self-contained HTML evidence, or a plain download fallback). It is source-
 * agnostic: the caller supplies a `fetchUrl` closure that resolves a
 * DocumentDownloadUrlResponse (an expediente document, an audit-evidence blob…),
 * plus a stable `cacheKey` so React Query can cache/dedupe the fetch.
 */
import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { DocumentDownloadUrlResponse } from '@cumpli/shared';
import { useToast } from '../lib/use-toast';
import { Dialog, Skeleton } from './ui';
import { Download } from 'lucide-react';

export function DocumentPreviewDialog({
  title,
  cacheKey,
  fetchUrl,
  onClose,
}: {
  title: string;
  /** Stable identity for the React Query cache (e.g. the docId or audit eventKey). */
  cacheKey: string;
  fetchUrl: () => Promise<DocumentDownloadUrlResponse>;
  onClose: () => void;
}) {
  const toast = useToast();
  const notifiedError = useRef(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['document-preview', cacheKey],
    queryFn: fetchUrl,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (isError && !notifiedError.current) {
      notifiedError.current = true;
      toast({ message: 'No se pudo cargar la vista previa', kind: 'error' });
    }
  }, [isError, toast]);

  const isImage = data ? data.mimeType.startsWith('image/') && data.mimeType !== 'image/tiff' : false;
  const isPdf = data?.mimeType === 'application/pdf';
  const isHtml = data?.mimeType === 'text/html';

  return (
    <Dialog open size="lg" title={`Vista previa — ${title}`} onClose={onClose}>
      <div className="space-y-4">
        {isLoading ? (
          <Skeleton className="h-96 w-full" />
        ) : isError || !data ? (
          <p className="text-sm text-tone-red">No se pudo cargar la vista previa</p>
        ) : isImage ? (
          <img src={data.url} alt={title} className="max-h-[70vh] w-full object-contain" />
        ) : isPdf ? (
          <iframe src={data.url} title={title} className="h-[70vh] w-full rounded-lg border border-line" />
        ) : isHtml ? (
          // Evidence is self-contained HTML from a presigned URL. Sandbox with NO
          // tokens: never allow-same-origin + allow-scripts together — the frame
          // renders as inert, opaque-origin markup (no scripts, no ambient auth).
          <iframe
            src={data.url}
            title={title}
            sandbox=""
            className="h-[70vh] w-full rounded-lg border border-line bg-panel"
          />
        ) : (
          <div className="grid place-items-center gap-2 rounded-lg border border-line bg-canvas px-6 py-16 text-center">
            <p className="text-sm text-ink-soft">No hay vista previa disponible para este archivo.</p>
            <a
              href={data.url}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-semibold text-primary hover:underline"
            >
              Descargar archivo
            </a>
          </div>
        )}

        {data && (
          <div className="flex justify-end">
            <a
              href={data.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-lg border border-line-strong bg-panel px-4 py-2.5 text-sm font-semibold text-ink hover:border-ink-faint"
            >
              <Download className="h-4 w-4" strokeWidth={2} /> Descargar
            </a>
          </div>
        )}
      </div>
    </Dialog>
  );
}
